import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Package, Search, Eye, Edit, Plus, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import useCustomFields from '../hooks/useCustomFields';
import CustomFieldCell from '../components/custom-fields/CustomFieldCell';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ViewMaterials = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [materialTypes, setMaterialTypes] = useState([]);

  const { fields: listCustomFields } = useCustomFields('material', { listViewOnly: true });

  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/materials?limit=1000`);
      setMaterials(response.data || []);
    } catch (error) {
      console.error('Error loading materials:', error);
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMaterialTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-types`);
      setMaterialTypes(response.data || []);
    } catch (error) {
      console.error('Error loading material types:', error);
    }
  }, []);

  const filterMaterials = useCallback(() => {
    let filtered = materials;
    if (searchTerm) {
      filtered = filtered.filter(
        (m) =>
          m.material_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.material_group?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterType !== 'all') {
      filtered = filtered.filter((m) => m.material_type === filterType);
    }
    setFilteredMaterials(filtered);
  }, [materials, searchTerm, filterType]);

  useEffect(() => {
    loadMaterials();
    loadMaterialTypes();
  }, [loadMaterials, loadMaterialTypes]);

  useEffect(() => {
    filterMaterials();
  }, [filterMaterials]);

  const getMaterialTypeName = (typeId) => {
    const type = materialTypes.find((t) => t.material_type_id === typeId);
    return type ? type.description : typeId;
  };

  const columns = [
    { key: 'material_number', header: 'Material Number', filterable: true, accessor: (m) => m.material_number,
      render: (m) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
            <Package className="w-4 h-4 text-orange-600" />
          </div>
          <div className="font-medium text-gray-900">{m.material_number}</div>
        </div>
      ) },
    { key: 'description', header: 'Description', filterable: true, accessor: (m) => m.description },
    { key: 'type', header: 'Type', accessor: (m) => getMaterialTypeName(m.material_type) },
    { key: 'material_group', header: 'Material Group', accessor: (m) => m.material_group },
    { key: 'base_uom', header: 'Base UoM', accessor: (m) => m.base_uom },
    { key: 'status', header: 'Status',
      render: (m) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${m.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {m.status}
        </span>
      ) },
    ...listCustomFields.map((f) => ({
      key: `cf_${f.fieldname}`, header: f.label,
      render: (m) => (
        <span data-testid={`material-cf-${f.fieldname}-${m.material_number}`}>
          <CustomFieldCell field={f} value={m.custom_data?.[f.fieldname]} />
        </span>
      ),
    })),
    { key: 'actions', header: 'Actions', align: 'right',
      render: (m) => (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => navigate(`/materials/view/${m.material_number}`)} className="text-orange-600 hover:text-orange-900 hover:bg-orange-50">
            <Eye className="w-4 h-4 mr-1" />View
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/materials/edit/${m.material_number}`)} className="text-orange-600 hover:text-orange-900 hover:bg-orange-50">
            <Edit className="w-4 h-4 mr-1" />Edit
          </Button>
        </div>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Materials</h1>
              <p className="text-sm text-gray-600 mt-1">View and manage all material master records</p>
            </div>
            <Button onClick={() => navigate('/material-creation')} className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4" />
              Create Material
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by material number, description, or group..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Material Types</option>
                  {materialTypes.map((type) => (
                    <option key={type.material_type_id} value={type.material_type_id}>
                      {type.material_type_id} - {type.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Materials Table */}
        <Card className="p-6">
          <DataTable
            testid="materials-table"
            columns={columns}
            data={filteredMaterials}
            loading={loading}
            rowKey={(m) => m.material_number}
            emptyText={searchTerm || filterType !== 'all' ? 'No materials found' : 'No materials yet'}
            emptyHint={searchTerm || filterType !== 'all' ? 'Try adjusting your search or filter criteria' : 'Get started by creating your first material'}
            rowProps={(m) => ({ 'data-testid': `material-${m.material_number}` })}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default ViewMaterials;
